import React, { createContext, useContext, ReactNode } from 'react';
import { useHeaderTheme, HeaderTheme } from '../hooks/useHeaderTheme';

interface HeaderThemeContextType {
  currentTheme: HeaderTheme;
  isScrolled: boolean;
  registerSection: (element: HTMLElement, sectionId: string, theme: HeaderTheme) => void;
  unregisterSection: (element: HTMLElement, sectionId: string) => void;
}

const HeaderThemeContext = createContext<HeaderThemeContextType | undefined>(undefined);

export function HeaderThemeProvider({ children }: { children: ReactNode }) {
  const headerTheme = useHeaderTheme();

  // Reflect current theme on document body for debugging/CSS hooks
  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.setAttribute('data-header-theme', headerTheme.currentTheme);
    }
  }, [headerTheme.currentTheme]);

  return <HeaderThemeContext.Provider value={headerTheme}>{children}</HeaderThemeContext.Provider>;
}

export function useHeaderThemeContext() {
  const context = useContext(HeaderThemeContext);
  if (context === undefined) {
    throw new Error('useHeaderThemeContext must be used within a HeaderThemeProvider');
  }
  return context;
}

// Hook for sections to register themselves
export function useSection(sectionId: string, theme: HeaderTheme) {
  const { registerSection, unregisterSection } = useHeaderThemeContext();

  const ref = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    const element = ref.current;
    if (element) {
      registerSection(element, sectionId, theme);

      return () => {
        unregisterSection(element, sectionId);
      };
    }
  }, [sectionId, theme, registerSection, unregisterSection]);

  return ref;
}
